import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSchool, useSchoolSchedules, useSchoolCalendar } from '@/hooks/use-schools';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Clock,
  Settings,
  AlertCircle,
  Copy,
  Check,
  Globe,
  Building,
  Home,
} from 'lucide-react';
import { toast } from 'sonner';
import { SchoolOverviewTab } from '@/app/(main)/dashboard/schools/[schoolId]/_components/school-overview-tab';
import { SchoolSchedulesTab } from '@/app/(main)/dashboard/schools/[schoolId]/_components/school-schedules-tab';
import { SchoolSettingsTab } from '@/app/(main)/dashboard/schools/[schoolId]/_components/school-settings-tab';
import { SchoolSetupWizard } from '@/app/(main)/dashboard/schools/[schoolId]/_components/school-setup-wizard';
import { usePreferencesStore } from '@/stores/preferences/preferences-provider';

export function SchoolDetailPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: school, isLoading, error } = useSchool(schoolId!);
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const isCollege = school?.organizationType === 'college';
  const isWaitlisted = school?.status === 'waitlist';
  
  // Only fetch schedule and calendar data for non-college organizations
  const { data: schedules } = useSchoolSchedules(schoolId!, !isCollege);
  const { data: calendar } = useSchoolCalendar(schoolId!, !isCollege);
  const [copiedId, setCopiedId] = useState(false);
  
  // Initialize activeTab from URL hash
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    return hash || 'overview';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [openSettingsSection, setOpenSettingsSection] = useState<string | null>(null);
  
  // Update URL hash when tab changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  const copySchoolId = async () => {
    if (school?.schoolName) {
      await navigator.clipboard.writeText(school.schoolName);
      setCopiedId(true);
      toast.success('School ID copied to clipboard');
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const handleNavigateToSettings = () => {
    setOpenSettingsSection('calendar');
    handleTabChange('settings');
  };

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  if (isLoading) {
    return (
      <div className="-m-6 animate-in fade-in-0 duration-300" style={{ transition: 'opacity 0.3s ease-in-out' }}>
        {/* Header Banner Skeleton */}
        <div className="relative overflow-hidden bg-background">
          <div className="container mx-auto px-6 pt-6">
            <div className="relative overflow-hidden rounded-lg py-8 bg-gradient-to-r from-primary/20 to-primary/5">
              <div className="flex items-center gap-4 px-6">
                {/* Logo Skeleton */}
                <Skeleton className="h-12 w-12 rounded-lg shrink-0" />
                
                {/* Title and Info Skeleton */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-7 w-64" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-4 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="container mx-auto px-6 m-6">
          <div className="space-y-4">
            {/* TabsList Skeleton */}
            <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 gap-1">
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-28" />
            </div>

            {/* Tab Content Skeleton - Calendar Layout */}
            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[1fr_400px] items-start">
                {/* Left Column - Calendar Skeleton */}
                <div className="space-y-4">
                  {/* Calendar Header Skeleton */}
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-16" />
                  </div>

                  {/* Legend Skeleton */}
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-24" />
                  </div>

                  {/* Calendar Grid Skeleton */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-7 gap-1.5 mb-2">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                          <div key={index} className="text-center font-semibold text-xs text-muted-foreground py-1.5">
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {Array.from({ length: 35 }).map((_, i) => (
                          <Skeleton
                            key={i}
                            className="min-h-[90px] rounded-lg"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Configuration Card Skeleton */}
                <div className="flex flex-col gap-4 pt-[32px]">
                  {/* Build Calendar Button Skeleton */}
                  <div className="flex justify-end">
                    <Skeleton className="h-10 w-40" />
                  </div>

                  {/* Configuration Card */}
                  <Card className="flex-1">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-7 w-7" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Schedule Type & Lunch Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Skeleton className="h-7 w-7 rounded-md" />
                          <Skeleton className="h-6 w-24" />
                        </div>
                        <div className="space-y-2">
                          <Skeleton className="h-7 w-7 rounded-md" />
                          <Skeleton className="h-6 w-20" />
                        </div>
                      </div>

                      <Skeleton className="h-px w-full" />

                      {/* Day Types */}
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <div className="flex flex-wrap gap-1.5">
                          <Skeleton className="h-6 w-16" />
                          <Skeleton className="h-6 w-16" />
                          <Skeleton className="h-6 w-16" />
                        </div>
                      </div>

                      {/* Period Types */}
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <div className="flex flex-wrap gap-1.5">
                          <Skeleton className="h-6 w-20" />
                          <Skeleton className="h-6 w-20" />
                          <Skeleton className="h-6 w-20" />
                        </div>
                      </div>

                      <Skeleton className="h-px w-full" />

                      {/* Rotation Pattern */}
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <div className="flex flex-wrap gap-1.5">
                          <Skeleton className="h-6 w-12" />
                          <Skeleton className="h-6 w-12" />
                          <Skeleton className="h-6 w-12" />
                          <Skeleton className="h-6 w-12" />
                        </div>
                </div>
              </CardContent>
            </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="container mx-auto px-6 py-6 m-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message || 'School not found'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="-m-6 animate-in fade-in-0 duration-700 ease-in-out" style={{ transition: 'opacity 0.7s ease-in-out' }}>
      {/* Header Banner - Full Width */}
      <div className="relative overflow-hidden bg-background">
        <div className="container mx-auto px-6 pt-6">
      <div 
            className="relative overflow-hidden rounded-lg py-8"
        style={school.primaryColor ? {
              backgroundImage: `radial-gradient(circle at left center, ${school.primaryColor}99 0%, ${school.primaryColor}66 25%, transparent 70%)`
            } : {
              background: 'var(--primary)'
            }}
      >
            <div className="flex items-center gap-4 px-6">
            {school.logo?.resourceUrl && (
              <div 
                className="h-12 w-12 rounded-lg border-2 bg-white p-1.5 shadow-lg shrink-0"
                style={{ borderColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                <img
                  src={school.logo.resourceUrl}
                  alt={`${school.schoolTitle} mascot`}
                  className="h-full w-full object-contain"
                />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className={`text-2xl font-bold tracking-tight truncate ${themeMode === 'light' ? 'text-black' : 'text-white'}`}>
                  {school.schoolTitle}
                </h1>
                <Badge
                  variant={
                    school.status === 'live'
                      ? 'default'
                      : school.status === 'waitlist'
                        ? 'secondary'
                        : 'destructive'
                  }
                  className="text-xs shrink-0"
                >
                  {school.status.toUpperCase()}
                </Badge>
              </div>
              <div className={`flex items-center gap-3 text-xs ${themeMode === 'light' ? 'text-black/70' : 'text-white/80'}`}>
                <button
                  onClick={copySchoolId}
                  className={`flex items-center gap-1.5 font-mono transition-colors ${themeMode === 'light' ? 'hover:text-black' : 'hover:text-white'}`}
                >
                  <span>ID: {school.schoolName}</span>
                  {copiedId ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
                {school.website && (
                  <>
                    <span>•</span>
                    <a
                      href={school.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center transition-colors ${themeMode === 'light' ? 'hover:text-black' : 'hover:text-white'}`}
                      title={school.website}
                    >
                      <Globe className="h-3.5 w-3.5" />
                    </a>
                  </>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Show Setup Wizard for Waitlisted Schools (not colleges) */}
      {isWaitlisted && !isCollege ? (
        <div className="container mx-auto px-6 py-6 m-6">
          <SchoolSetupWizard school={school} schoolId={schoolId!} />
        </div>
      ) : (
        <>
      {/* Tabs */}
      <div className="container mx-auto px-6 m-6">
        {isCollege && (
          <Alert className="mb-4">
            <Building className="h-4 w-4" />
            <AlertDescription>
              This is a college. Calendar and schedule management features are not available for colleges.
            </AlertDescription>
          </Alert>
        )}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList
          className="border-2 p-1 relative backdrop-blur-sm rounded-lg"
          style={school.primaryColor ? {
            borderColor: `${school.primaryColor}40`,
            background: `${school.primaryColor}08`
          } : {}}
        >
          <TabsTrigger 
            value="overview"
            className="relative transition-all duration-300 ease-in-out hover:scale-[1.02] hover:-translate-y-0.5 active:scale-95"
            style={activeTab === 'overview' && school.primaryColor ? {
              backgroundColor: school.primaryColor,
              color: getContrastColor(school.primaryColor)
            } : {}}
          >
            <Home className="mr-2 h-4 w-4 transition-all duration-300" />
            <span className="font-medium transition-all">Overview</span>
          </TabsTrigger>
          {!isCollege && (
            <TabsTrigger 
              value="schedules"
              className="relative transition-all duration-300 ease-in-out hover:scale-[1.02] hover:-translate-y-0.5 active:scale-95"
              style={activeTab === 'schedules' && school.primaryColor ? {
                backgroundColor: school.primaryColor,
                color: getContrastColor(school.primaryColor)
              } : {}}
            >
              <Clock className="mr-2 h-4 w-4 transition-all duration-300" />
              <span className="font-medium transition-all">Schedules</span>
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="settings"
            className="relative transition-all duration-300 ease-in-out hover:scale-[1.02] hover:-translate-y-0.5 active:scale-95"
            style={activeTab === 'settings' && school.primaryColor ? {
              backgroundColor: school.primaryColor,
              color: getContrastColor(school.primaryColor)
            } : {}}
          >
            <Settings className="mr-2 h-4 w-4 transition-all duration-300 data-[state=active]:rotate-90" />
            <span className="font-medium transition-all">Settings</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent 
          value="overview" 
          className="space-y-4 animate-in fade-in-0 duration-300 ease-in-out"
        >
          <SchoolOverviewTab 
            school={school} 
            schoolId={schoolId!} 
            onNavigateToSettings={handleNavigateToSettings}
          />
        </TabsContent>

        {!isCollege && (
          <TabsContent 
            value="schedules" 
            className="space-y-4 animate-in fade-in-0 duration-300 ease-in-out"
          >
            <SchoolSchedulesTab schoolId={schoolId!} />
          </TabsContent>
        )}

        <TabsContent 
          value="settings" 
          className="space-y-4 animate-in fade-in-0 duration-300 ease-in-out"
        >
          <SchoolSettingsTab 
            school={school} 
            openSection={openSettingsSection}
            onSectionOpened={() => setOpenSettingsSection(null)}
          />
        </TabsContent>
        </Tabs>
      </div>
        </>
      )}
    </div>
  );
}